import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import TagProductsPanel from "@/components/console/etchery/TagProductsPanel";
import TagOrdersPanel from "@/components/console/etchery/TagOrdersPanel";
import WateringBlockLabPanel from "@/components/console/etchery/WateringBlockLabPanel";
import {
  LayoutDashboard, Tag, Package, Droplets, Users as UsersIcon,
  Boxes, Coins, BarChart3, StickyNote, AlertCircle, Plus, Trash2, X, Edit2,
} from "lucide-react";

const SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "tags", label: "Plant Tags", icon: Tag },
  { id: "orders", label: "Tag Orders", icon: Package },
  { id: "blocks", label: "Watering Block Program", icon: Droplets },
  { id: "growers", label: "Grower Watering Database", icon: UsersIcon },
  { id: "supplies", label: "Supplies", icon: Boxes },
  { id: "financials", label: "Financials", icon: Coins },
  { id: "reports", label: "Reports", icon: BarChart3 },
  { id: "notes", label: "Etchery Notes", icon: StickyNote },
];

const CATEGORIES = [
  { v: "general", label: "General" },
  { v: "production", label: "Production" },
  { v: "design", label: "Design" },
  { v: "supply", label: "Supply" },
  { v: "meeting", label: "Meeting" },
  { v: "block_dev", label: "Block Development" },
];

export default function ConsoleEtchery() {
  const [section, setSection] = useState("dashboard");

  return (
    <div data-testid="console-etchery">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-1">Heritage Bloom Club × The Etchery</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-none">The Etchery Partnership</h1>
        <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] mt-3">
          Plant tags, watering blocks, supplies, and shared notes. The operational heartbeat of the partnership.
        </p>
      </div>

      {/* Sub-nav */}
      <div className="flex flex-wrap gap-1 mb-8 border-b border-[var(--bloom-border)] -mx-1" data-testid="etchery-subnav">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = section === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`px-3 py-2.5 mx-1 mb-[-1px] border-b-2 text-sm transition flex items-center gap-2 ${
                active
                  ? "border-[var(--bloom-ink)] text-[var(--bloom-ink)]"
                  : "border-transparent text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink-soft)]"
              }`}
              data-testid={`etchery-tab-${s.id}`}
            >
              <Icon size={14} strokeWidth={1.5} />
              <span className="font-serif">{s.label}</span>
            </button>
          );
        })}
      </div>

      {section === "dashboard" && <DashboardPanel />}
      {section === "tags" && <TagProductsPanel />}
      {section === "orders" && <TagOrdersPanel />}
      {section === "blocks" && <WateringBlockLabPanel />}
      {section === "growers" && <PlaceholderPanel title="Grower Watering Database" milestone="E3"
        copy="One row per Bloom Club grower (auto-seeded from the existing grower list). Each profile tracks subscriber mix, calculated block requirement, production status (8 stages), dispatch logistics, replacement history, and admin overrides."
        items={[
          "Auto-seeded from existing grower list",
          "Calculator: subscribers × mix × frequency × spare% × replacement allowance",
          "8-stage production workflow per grower",
          "Tracking, dispatch + receive dates, replacement history",
        ]}
      />}
      {section === "supplies" && <PlaceholderPanel title="Supplies" milestone="E4"
        copy="All raw materials used by The Etchery — tag bases, acrylic, timber, colour, packaging, block blanks, finishing products. Low-stock alerts fire automatically when current stock falls below the reorder level."
        items={[
          "Per-item supplier, unit cost, stock, reorder level + qty",
          "Categories: Plant Tags / Watering Blocks / Shared",
          "Automatic low-stock warnings",
        ]}
      />}
      {section === "financials" && <PlaceholderPanel title="Financials" milestone="E4"
        copy="Partnership-level revenue, costs, and split tracking. Supports both Fixed Cost and Percentage Split models. Stores amount owed to Etchery, amount retained by Bloom Club, payment dates and invoice references."
        items={[
          "Revenue from tag orders (monthly + annual)",
          "Production costs across tags, blocks, packaging, distribution",
          "Fixed Cost or % Split model with payment ledger",
        ]}
      />}
      {section === "reports" && <PlaceholderPanel title="Reports" milestone="E4"
        copy="Analytics across the partnership: most popular tag styles + colours, top plant varieties, revenue by month/grower, blocks supplied vs outstanding, cost per grower, cost per territory, replacement rate, supply usage history."
        items={[
          "Tag analytics: styles, colours, plants, monthly revenue + profit",
          "Block analytics: growers supplied, cost per territory, replacement rate",
          "Supplies: usage history, inventory value, low-stock trends",
        ]}
      />}
      {section === "notes" && <NotesPanel />}
    </div>
  );
}

/* ============================ Dashboard ============================== */
function DashboardPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/etchery/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, []);

  if (err) return <ErrorBox msg={err} />;
  if (!data) return <Loading />;

  const tilesByGroup = [
    {
      title: "Plant Tags",
      tiles: [
        { label: "Orders this month", v: data.tags.orders_this_month },
        { label: "In production", v: data.tags.in_production },
        { label: "Completed", v: data.tags.completed },
        { label: "Revenue NZD", v: nzd(data.tags.revenue_nzd) },
        { label: "Production cost", v: nzd(data.tags.production_cost_nzd) },
        { label: "Gross profit", v: nzd(data.tags.gross_profit_nzd) },
      ],
    },
    {
      title: "Watering Blocks",
      tiles: [
        { label: "Growers requiring", v: data.watering_blocks.growers_requiring },
        { label: "Blocks required", v: data.watering_blocks.blocks_required },
        { label: "Produced", v: data.watering_blocks.blocks_produced },
        { label: "Dispatched", v: data.watering_blocks.blocks_dispatched },
        { label: "Outstanding", v: data.watering_blocks.blocks_outstanding },
        { label: "Project cost", v: nzd(data.watering_blocks.project_cost_nzd) },
      ],
    },
    {
      title: "Supplies",
      tiles: [
        { label: "Low stock alerts", v: data.supplies.low_stock_alerts },
        { label: "Due for reorder", v: data.supplies.due_for_reorder },
        { label: "Inventory value", v: nzd(data.supplies.inventory_value_nzd) },
      ],
    },
    {
      title: "Financial Summary",
      tiles: [
        { label: "Revenue", v: nzd(data.financials.revenue_nzd) },
        { label: "Costs", v: nzd(data.financials.costs_nzd) },
        { label: "Profit", v: nzd(data.financials.profit_nzd) },
        { label: "Owed to Etchery", v: nzd(data.financials.owed_to_etchery_nzd) },
        { label: "Retained by Bloom", v: nzd(data.financials.retained_by_bloom_nzd) },
      ],
    },
  ];

  return (
    <div className="space-y-10" data-testid="etchery-dashboard">
      <div className="bloom-card p-4 flex items-start gap-3 bg-[var(--bloom-surface)]">
        <AlertCircle size={18} className="text-[var(--bloom-warning)] shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="font-serif text-sm italic text-[var(--bloom-ink-soft)]">
          This dashboard is the scaffold for the partnership console. Numbers populate as the Plant Tags (E2), Watering Block (E3) and Supplies/Financials (E4) modules ship. Notes (below) is live now.
        </div>
      </div>

      {tilesByGroup.map((group) => (
        <div key={group.title}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-3">{group.title}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {group.tiles.map((t) => (
              <div key={t.label} className="bloom-card p-4">
                <div className="bloom-label mb-2">{t.label}</div>
                <div className="font-serif text-2xl">{t.v}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================ Placeholder ============================ */
function PlaceholderPanel({ title, copy, items, milestone }) {
  return (
    <div className="bloom-card p-10 text-center max-w-3xl mx-auto" data-testid={`etchery-placeholder-${(title || "").toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-2">
        {milestone || "Coming soon"}
      </div>
      <h2 className="font-serif text-3xl mb-4">{title}</h2>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] leading-relaxed mb-6">{copy}</p>
      {items && (
        <ul className="text-left max-w-xl mx-auto space-y-2 text-sm">
          {items.map((it) => (
            <li key={it} className="flex gap-3">
              <span className="text-[var(--bloom-brand)]">✦</span>
              <span className="font-serif">{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================ Notes feed ============================= */
function NotesPanel() {
  const [notes, setNotes] = useState([]);
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState(null); // { id?, title, body, category }
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/etchery/notes${category ? `?category=${category}` : ""}`);
    setNotes(data.notes || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [category]);

  const save = async (n) => {
    if (n.id) {
      await api.patch(`/admin/etchery/notes/${n.id}`, { title: n.title, body: n.body, category: n.category });
    } else {
      await api.post("/admin/etchery/notes", { title: n.title, body: n.body, category: n.category });
    }
    setEditing(null);
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/admin/etchery/notes/${id}`);
    await load();
  };

  return (
    <div data-testid="etchery-notes">
      <div className="flex items-center gap-2 mb-6">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="bloom-input max-w-xs" data-testid="notes-category-filter">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
        <button
          onClick={() => setEditing({ title: "", body: "", category: "general" })}
          className="bloom-btn bloom-btn-primary ml-auto"
          data-testid="notes-new-btn"
        >
          <Plus size={14} /> New note
        </button>
      </div>

      {loading ? (
        <Loading />
      ) : notes.length === 0 ? (
        <Empty msg="No notes yet. Drop the first one to start the shared log." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="notes-grid">
          {notes.map((n) => (
            <article key={n.id} className="bloom-card p-5" data-testid={`note-card-${n.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-serif text-xl leading-tight">{n.title}</h3>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(n)} title="Edit" className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid={`note-edit-${n.id}`}><Edit2 size={13} /></button>
                  <button onClick={() => remove(n.id)} title="Delete" className="p-1.5 hover:bg-[var(--bloom-surface)] text-[var(--bloom-accent)]" data-testid={`note-delete-${n.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mb-3">
                <span className="border border-[var(--bloom-border)] px-1.5 py-0.5">{CATEGORIES.find((c) => c.v === n.category)?.label || n.category}</span>
                <span>{new Date(n.created_at).toLocaleDateString()}</span>
                <span>· {n.author_name}</span>
              </div>
              <p className="font-serif text-sm whitespace-pre-line leading-relaxed text-[var(--bloom-ink)]">{n.body}</p>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <NoteEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function NoteEditor({ initial, onClose, onSave }) {
  const [form, setForm] = useState({ title: "", body: "", category: "general", ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return setErr("Title and body required.");
    setBusy(true); setErr("");
    try {
      await onSave({ ...form, title: form.title.trim(), body: form.body.trim() });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="note-editor-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <h2 className="font-serif text-2xl">{form.id ? "Edit note" : "New note"}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <div className="bloom-label mb-2">Title</div>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} className="bloom-input" autoFocus data-testid="note-title-input" />
          </div>
          <div>
            <div className="bloom-label mb-2">Category</div>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="bloom-input" data-testid="note-category-input">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div className="bloom-label mb-2">Body</div>
            <textarea rows={6} value={form.body} onChange={(e) => set("body", e.target.value)} className="bloom-input" data-testid="note-body-input" />
          </div>
          {err && <div className="flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic"><AlertCircle size={14} /> {err}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="note-save-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================ Bits =================================== */
function nzd(v) {
  const n = Number(v) || 0;
  return `$${n.toLocaleString()}`;
}
function Loading() { return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">Loading…</div>; }
function Empty({ msg }) { return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">{msg}</div>; }
function ErrorBox({ msg }) { return <div className="bloom-card p-6 text-[var(--bloom-accent)] font-serif italic flex items-center gap-2"><AlertCircle size={14} /> {msg}</div>; }
